import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  TextInput, 
  toast,
  Select,
  DataTable,
  Switch,
  Badge,
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC, usePermissions } from '../../hooks/usePermissions';
import { useFarmers } from '../../hooks/useFarmers';
import { farmerService } from '../../services/farmerService';
import type { Farmer } from '../../types/farmer';
import type { FarmerFilters } from '../../types/farmer';
import type { ActionDefinition } from '../../utils/tableHelpers';
import { PermissionGate } from '../../components/PermissionGate';
import { handleGenerateFarmerPdf } from '../../utils/pdfUtils';
import type { AdministrativeUnit } from '../../types/administrativeUnit';
import { getProvinces, getDistrictsForProvince, getWardsForDistrict } from '../../services/administrativeUnitService';

import {
  EditIcon,
  UserXIcon,
  SearchIcon,
  Users2Icon,
  FilterIcon,
  FileTextIcon,
  EyeIcon,
  Upload,
  ArrowUpDown,
} from 'lucide-react';

import { 
  textColumn,
  dateColumn,
  numberColumn
} from '../../utils/columns';

import { BulkUploadModal } from '../../components/common/BulkUploadModal';

const SORT_FIELD_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'name', label: 'Name' },
  { value: 'farmerCode', label: 'Farmer Code' },
  { value: 'phone', label: 'Phone' },
  { value: 'county', label: 'County' },
  { value: 'crop', label: 'Crop' },
  { value: 'area', label: 'Area' },
];

const SORT_ORDER_OPTIONS = [
  { value: 'desc', label: 'Descending' },
  { value: 'asc', label: 'Ascending' },
];

export function FarmerManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { isAdmin } = useRBAC();
  const { userPermissions } = usePermissions();
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Admin unit state for cascading selects
  const [provinces, setProvinces] = useState<AdministrativeUnit[]>([]);
  const [districts, setDistricts] = useState<AdministrativeUnit[]>([]);
  const [wards, setWards] = useState<AdministrativeUnit[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<string>('all');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('all');
  const [selectedWard, setSelectedWard] = useState<string>('all');

  const {
    filteredFarmers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    viewAllOrgs,
    setViewAllOrgs,
    sorting,
    setSorting,
    pagination,
    totalFarmers,
    refreshFarmers,
    fetchFarmers,
    updateFarmerOptimistically,
    deleteFarmerOptimistically,
  } = useFarmers();

  // Restore page from URL search params on initial mount only
  const [initialPageRestored, setInitialPageRestored] = useState(false);
  useEffect(function restorePageFromUrl() {
    if (initialPageRestored) return;
    setInitialPageRestored(true);
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page > 1) {
        fetchFarmers(page, filters, pagination.limit, viewAllOrgs, sorting);
      }
    }
  }, [initialPageRestored, searchParams, fetchFarmers, filters, pagination.limit, viewAllOrgs, sorting]);

  // Load provinces on mount
  useEffect(function loadProvinces() {
    getProvinces('ZW').then(setProvinces).catch(console.error);
  }, []);

  // Load districts when province changes
  useEffect(function loadDistricts() {
    if (selectedProvince && selectedProvince !== 'all') {
      getDistrictsForProvince(selectedProvince).then(setDistricts).catch(console.error);
    } else {
      setDistricts([]);
    }
    setSelectedDistrict('all');
    setSelectedWard('all');
    setWards([]);
  }, [selectedProvince]);

  // Load wards when district changes
  useEffect(function loadWards() {
    if (selectedDistrict && selectedDistrict !== 'all') {
      getWardsForDistrict(selectedDistrict).then(setWards).catch(console.error);
    } else {
      setWards([]);
    }
    setSelectedWard('all');
  }, [selectedDistrict]);

  // Handle optimistic updates from navigation state (e.g., returning from edit)
  useEffect(function handleReturnFromEdit() {
    const state = location.state as { updatedFarmer?: Farmer } | null;
    if (state?.updatedFarmer) {
      updateFarmerOptimistically(state.updatedFarmer.id, state.updatedFarmer);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state, updateFarmerOptimistically, navigate, location.pathname, location.search]);

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    fetchFarmers(1, filters, pagination.limit, viewAllOrgs, sorting);
  }, [filters, pagination.limit, viewAllOrgs, fetchFarmers, setSearchQuery, sorting]);

  // Handle admin unit filter
  const handleAdminUnitFilter = useCallback((level: number, adminCode: string) => {
    const newFilters: FarmerFilters = {
      ...filters,
      administrativeLevel: adminCode === 'all' ? undefined : { level, value: adminCode },
      county: undefined,
    };
    setFilters(newFilters);
    fetchFarmers(1, newFilters, pagination.limit, viewAllOrgs, sorting);
  }, [filters, setFilters, fetchFarmers, pagination.limit, viewAllOrgs, sorting]);

  // Handle crop filter
  const handleCropFilter = useCallback((crop: string) => {
    const newFilters = {
      ...filters,
      crop: crop === 'all' ? undefined : crop,
    };
    setFilters(newFilters);
    fetchFarmers(1, newFilters, pagination.limit, viewAllOrgs, sorting);
  }, [filters, setFilters, fetchFarmers, pagination.limit, viewAllOrgs, sorting]);

  // Handle sort change
  const handleSortChange = useCallback((field: string) => {
    const newSort = { sortBy: field, sortOrder: sorting.sortOrder as 'asc' | 'desc' };
    setSorting(newSort);
    fetchFarmers(1, filters, pagination.limit, viewAllOrgs, newSort);
  }, [sorting.sortOrder, setSorting, fetchFarmers, filters, pagination.limit, viewAllOrgs]);

  const handleSortOrderChange = useCallback((order: string) => {
    const newSort = { sortBy: sorting.sortBy, sortOrder: order as 'asc' | 'desc' };
    setSorting(newSort);
    fetchFarmers(1, filters, pagination.limit, viewAllOrgs, newSort);
  }, [sorting.sortBy, setSorting, fetchFarmers, filters, pagination.limit, viewAllOrgs]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setSearchQuery('');
    setSelectedProvince('all');
    setSelectedDistrict('all');
    setSelectedWard('all');
    const defaultSort = { sortBy: 'createdAt', sortOrder: 'desc' as const };
    setSorting(defaultSort);
    fetchFarmers(1, emptyFilters, pagination.limit, viewAllOrgs, defaultSort);
  }, [setFilters, setSearchQuery, setSorting, fetchFarmers, pagination.limit, viewAllOrgs]);

  // Handle view all orgs toggle
  const handleViewAllOrgsToggle = useCallback((enabled: boolean) => {
    setViewAllOrgs(enabled);
    fetchFarmers(1, filters, pagination.limit, enabled, sorting);
  }, [setViewAllOrgs, fetchFarmers, filters, pagination.limit, sorting]);

  // Handle farmer actions
  const handleCreate = useCallback(() => {
    navigate('/admin/farmers/create');
  }, [navigate]);

  const handleBulkUploadComplete = useCallback((result: { valid_count?: number }) => {
    setBulkUploadOpen(false);
    showSuccess(
      'Farmers imported successfully!', 
      `${result?.valid_count || 0} farmers have been added to your organization.`
    );
    refreshFarmers();
  }, [showSuccess, refreshFarmers]);

  const handleBulkUploadError = useCallback((errorMsg: string) => {
    showError('Bulk upload failed', errorMsg);
  }, [showError]);

  const handleEdit = useCallback((farmer: Farmer) => {
    navigate(`/admin/farmers/${farmer.id}/edit`);
  }, [navigate]);

  const handleView = useCallback((farmer: Farmer) => {
    // #94: Store current page in URL so we can restore it on back navigation
    const params = new URLSearchParams(searchParams);
    params.set('page', pagination.page.toString());
    setSearchParams(params, { replace: true });
    navigate(`/admin/farmers/${farmer.id}`);
  }, [navigate, searchParams, setSearchParams, pagination.page]);

  const handleDeactivate = useCallback(async (farmer: Farmer) => {
    if (!confirm(`Are you sure you want to deactivate farmer "${farmer.name}"?\n\nThis will make the farmer inactive but preserve all data.`)) {
      return;
    }

    try {
      deleteFarmerOptimistically(farmer.id);
      showSuccess('Farmer Deactivated', `Farmer "${farmer.name}" has been deactivated successfully.`);
      await farmerService.deleteFarmer(farmer.id);
    } catch (err) {
      console.error('Error deactivating farmer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate farmer.';
      showError('Deactivation Failed', errorMessage);
      refreshFarmers();
    }
  }, [deleteFarmerOptimistically, showSuccess, showError, refreshFarmers]);

  const handleGeneratePdf = useCallback(async (farmer: Farmer) => {
    try {
      await handleGenerateFarmerPdf(farmer.id, farmer.name);
    } catch (err) {
      console.error('PDF generation error:', err);
    }
  }, []);

  const actionDefinitions: ActionDefinition<Farmer>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: <EyeIcon size={16} />,
      onClick: handleView,
      intent: 'outline',
      hidden: () => !userPermissions?.includes('farmers:read'),
    },
    {
      id: 'pdf',
      label: 'Generate PDF',
      icon: <FileTextIcon size={16} />,
      onClick: handleGeneratePdf,
      intent: 'secondary',
      hidden: () => !userPermissions?.includes('farmers:read'),
    },
    {
      id: 'edit',
      label: 'Edit Farmer',
      icon: <EditIcon size={16} />,
      onClick: handleEdit,
      intent: 'outline',
      hidden: (farmer) => {
        if (isAdmin()) return false;
        if (!userPermissions?.includes('farmers:update')) return true;
        return farmer.organizationId !== user?.organization?.id;
      },
    },
    {
      id: 'deactivate',
      label: 'Deactivate Farmer',
      icon: <UserXIcon size={16} />,
      onClick: handleDeactivate,
      intent: 'danger',
      disabled: (farmer) => !farmer.isActive,
      hidden: (farmer) => {
        if (isAdmin()) return false;
        if (!userPermissions?.includes('farmers:delete')) return true;
        return farmer.organizationId !== user?.organization?.id;
      },
    },
  ];

  const columns = [
    textColumn<Farmer>('farmerCode', {
      header: 'Code',
      width: 120,
      className: 'font-mono text-sm'
    }),
    textColumn<Farmer>('name', {
      header: 'Name',
      maxLength: 30
    }),
    textColumn<Farmer>('phone', {
      header: 'Phone',
      emptyDisplay: '-',
      maxLength: 20
    }),
    textColumn<Farmer>('county', {
      header: 'County',
      emptyDisplay: '-'
    }),
    textColumn<Farmer>('crop', {
      header: 'Crop',
      emptyDisplay: '-'
    }),
    numberColumn<Farmer>('area', {
      header: 'Area (ha)',
      emptyDisplay: '-',
      suffix: ' ha',
      precision: 1
    }),
    {
      id: 'geolocationStatus',
      header: 'Geo',
      enableSorting: false,
      size: 60,
      cell: ({ row }: { row: { original: Farmer } }) => {
        const status = row.original.geolocationStatus;
        if (!status) return <span className="text-gray-300">-</span>;
        const map: Record<string, { variant: 'success' | 'destructive' | 'secondary' | 'warning'; label: string }> = {
          valid: { variant: 'success', label: 'OK' },
          invalid: { variant: 'destructive', label: 'Fail' },
          unverified: { variant: 'secondary', label: '?' },
          no_coordinates: { variant: 'warning', label: 'N/C' },
          no_admin_unit: { variant: 'warning', label: 'N/A' },
        };
        const cfg = map[status];
        if (!cfg) return <span>{status}</span>;
        return <Badge variant={cfg.variant} className="text-xs">{cfg.label}</Badge>;
      },
    },
    textColumn<Farmer>('organization', {
      header: 'Organisation',
      getValue: (farmer) => (farmer as Farmer).organization.name
    }),
    dateColumn<Farmer>('createdAt', {
      header: 'Created',
      format: 'relative'
    }),
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      size: 140,
      cell: ({ row }: { row: { original: Farmer } }) => {
        const farmer = row.original;
        const visibleActions = actionDefinitions.filter(action => !action.hidden?.(farmer));
        return (
          <div className="flex items-center gap-2">
            {visibleActions.map((action) => (
              <button
                key={action.id}
                onClick={() => action.onClick(farmer)}
                disabled={action.disabled?.(farmer)}
                className={`p-1 rounded transition-colors ${
                  action.intent === 'danger' 
                    ? action.disabled?.(farmer) 
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-800'
                    : action.intent === 'secondary'
                      ? action.disabled?.(farmer)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-green-600 hover:text-green-800'
                      : action.disabled?.(farmer)
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-blue-600 hover:text-blue-800'
                }`}
                title={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  // Build admin unit filter options
  const provinceOptions = useMemo(() => [
    { value: 'all', label: 'All Provinces' },
    ...provinces.map(p => ({ value: p.adminCode || p.id, label: p.adminName })),
  ], [provinces]);

  const districtOptions = useMemo(() => [
    { value: 'all', label: 'All Districts' },
    ...districts.map(d => ({ value: d.adminCode || d.id, label: d.adminName })),
  ], [districts]);

  const wardOptions = useMemo(() => [
    { value: 'all', label: 'All Wards' },
    ...wards.map(w => ({ value: w.adminCode || w.id, label: w.adminName })),
  ], [wards]);

  // Crop filter: derive from currently loaded data (server filters by crop)
  const crops = useMemo(() => {
    const unique = Array.from(new Set(filteredFarmers.map(f => f.crop).filter(Boolean)));
    return [
      { value: 'all', label: 'All Crops' },
      ...unique.map(crop => ({ value: crop!, label: crop! })),
    ];
  }, [filteredFarmers]);

  const hasActiveFilters = searchQuery || filters.county || filters.crop || filters.administrativeLevel || sorting.sortBy !== 'createdAt' || sorting.sortOrder !== 'desc';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Farmer Management</h1>
          <p className="text-gray-600 mt-1">
            Manage farmer profiles, locations, and agricultural details
          </p>
        </div>
        <PermissionGate permission="farmers:create">
          <div className="flex space-x-3">
            <NeutralButton onClick={() => setBulkUploadOpen(true)}>
              <Upload className="w-4 h-4" />
              Bulk Upload
            </NeutralButton>
            
            <PositiveButton onClick={handleCreate}>
              <Users2Icon className="w-4 h-4" />
              Add Farmer
            </PositiveButton>
          </div>
        </PermissionGate>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary">{totalFarmers}</div>
          <div className="text-sm text-base-content/70">
            Total Farmers {viewAllOrgs ? '(All Organisations)' : '(Your Organisation)'}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <FilterIcon className="w-5 h-5" />
            Search and Filters
          </h3>
          <div className="flex items-center gap-4">
            {isAdmin() && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={viewAllOrgs}
                  onCheckedChange={handleViewAllOrgsToggle}
                  id="view-all-orgs"
                />
                <label htmlFor="view-all-orgs" className="text-sm font-medium text-gray-700 cursor-pointer">
                  View farmers from all organisations
                </label>
                {viewAllOrgs && (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    Admin Mode: All Organisations
                  </span>
                )}
              </div>
            )}
            {hasActiveFilters && (
              <NeutralButton onClick={handleClearFilters} className="text-sm">
                Clear All
              </NeutralButton>
            )}
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
          <div className="pl-10">
            <TextInput
              placeholder="Search farmers by name, code, phone..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Admin Unit Filter Row */}
        <div className="grid grid-cols-4 gap-4">
          <Select
            label="Province"
            options={provinceOptions}
            value={selectedProvince}
            onValueChange={(value: string) => {
              setSelectedProvince(value);
              handleAdminUnitFilter(1, value);
            }}
            placeholder="All Provinces"
          />
          <Select
            label="District"
            options={districtOptions}
            value={selectedDistrict}
            onValueChange={(value: string) => {
              setSelectedDistrict(value);
              if (value !== 'all') {
                handleAdminUnitFilter(2, value);
              } else if (selectedProvince !== 'all') {
                handleAdminUnitFilter(1, selectedProvince);
              } else {
                handleAdminUnitFilter(1, 'all');
              }
            }}
            placeholder="All Districts"
          />
          <Select
            label="Ward"
            options={wardOptions}
            value={selectedWard}
            onValueChange={(value: string) => {
              setSelectedWard(value);
              if (value !== 'all') {
                handleAdminUnitFilter(3, value);
              } else if (selectedDistrict !== 'all') {
                handleAdminUnitFilter(2, selectedDistrict);
              } else if (selectedProvince !== 'all') {
                handleAdminUnitFilter(1, selectedProvince);
              } else {
                handleAdminUnitFilter(1, 'all');
              }
            }}
            placeholder="All Wards"
          />
          <Select
            label="Crop"
            options={crops}
            value={filters.crop || 'all'}
            onValueChange={(value: string) => handleCropFilter(value)}
            placeholder="All Crops"
          />
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ArrowUpDown className="w-4 h-4" />
            Sort:
          </div>
          <div className="grid grid-cols-2 gap-4 flex-1 max-w-md">
            <Select
              options={SORT_FIELD_OPTIONS}
              value={sorting.sortBy}
              onValueChange={handleSortChange}
              placeholder="Sort by"
            />
            <Select
              options={SORT_ORDER_OPTIONS}
              value={sorting.sortOrder}
              onValueChange={handleSortOrderChange}
              placeholder="Order"
            />
          </div>
        </div>

        
        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="text-sm text-gray-600">
            Active filters: {[
              searchQuery && `Search: "${searchQuery}"`,
              filters.administrativeLevel && `Admin Unit: ${filters.administrativeLevel.value}`,
              filters.crop && `Crop: ${filters.crop}`,
              (sorting.sortBy !== 'createdAt' || sorting.sortOrder !== 'desc') && `Sort: ${sorting.sortBy} ${sorting.sortOrder}`,
            ].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Farmers</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <NeutralButton onClick={refreshFarmers}>
                  Try Again
                </NeutralButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Farmers Table - sorting disabled (handled server-side via sort controls above) */}
      <DataTable
        data={filteredFarmers}
        columns={columns}
        enableSorting={false}
        enableResizing={true}
        loading={loading}
        manualPagination={true}
        pageCount={pagination.totalPages}
        rowCount={pagination.total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1;
          const newPageSize = paginationState.pageSize;
          fetchFarmers(newPage, filters, newPageSize, viewAllOrgs, sorting);
        }}
        state={{
          pagination: {
            pageIndex: pagination.page - 1,
            pageSize: pagination.limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          enableFastNavigation: true,
          enableJumpToPage: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />

      {/* Bulk Upload Modal */}
      {bulkUploadOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="bg-white rounded-lg">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Bulk Upload Farmers
                  </h2>
                  <NeutralButton onClick={() => setBulkUploadOpen(false)}>
                    ✕
                  </NeutralButton>
                </div>
              </div>
              <div className="p-6">
                <BulkUploadModal
                  onUploadComplete={handleBulkUploadComplete}
                  onError={handleBulkUploadError}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
